import { useCallback, useEffect, useRef, type RefObject } from "react";
import type { ChatInputDictation } from "../../../components/ui/ChatInput";
import { useDictation, type DictationState } from "./useDictation";

// Thin composer-facing wrapper over the dictation state machine: a mic tap starts
// dictation at the caret when idle, or stops it when a session is active — record
// / stop / resume, as many times as the user likes, each session landing wherever
// the caret sits.
//
// Dedicated hook (the sanctioned home for the caret refs), keeping the composer
// components declarative.

// How long after stop the transcribing cancel ignores taps (double-tap guard).
const TRANSCRIBING_CANCEL_GRACE_MS = 600;

export type UseDictationControl = {
  state: DictationState;
  isSupported: boolean;
  // Mic engaged or transcript in flight (connecting/listening/transcribing) —
  // composer stays locked for this window.
  isActive: boolean;
  // Mic tapped — stops if a session is active, otherwise starts dictation.
  onMicTap: () => void;
  // Stop any active session (e.g. when the composer takes focus).
  stop: () => void;
  // The composer's send. Idle: sends at once. While a session is live: stops it,
  // waits for the transcript to land in the composer, then sends the result.
  send: () => void;
  // Stop and throw the transcript away, restoring the pre-dictation text.
  cancel: () => void;
  // The ChatInput-facing view of this control, owned HERE so every surface
  // renders the same faces — `null` where dictation is unsupported (the mic is
  // then never offered), and an error reads as `idle`, because all the user
  // can do about it is tap again.
  composer: ChatInputDictation | null;
  // The composer must refuse edits while the caret anchor is live but no text
  // has landed yet: during `connecting` (the anchor was snapshotted at mic-tap
  // and the first transcript is pending) and `transcribing` (the take's
  // transcript is in flight toward that anchor). While `listening`, editing
  // stays possible — focusing stops dictation so the keyboard opens (iOS won't
  // focus a readOnly field).
  composerReadOnly: boolean;
};

export function useDictationControl(params: {
  onTranscript: (text: string) => void;
  // The composer's textarea. Its caret decides where the transcript is inserted,
  // and is moved past the insertion when the session ends.
  textareaRef: RefObject<HTMLTextAreaElement>;
  // Sends the composer's current text (read at call time, after the transcript
  // landed).
  onSend: () => void;
  // Whether `onSend` may run right now: text to send and nothing blocking it.
  // Read after the transcript landed, to decide whether a send requested
  // mid-dictation goes ahead.
  canSend: () => boolean;
}): UseDictationControl {
  const textareaRef = params.textareaRef;
  // Where the caret belongs once this session ends. `null` between sessions —
  // stopping a session that never emitted must leave the caret where the user
  // put it (`stop` is also called on send, and on focusing the composer).
  const caretRef = useRef<number | null>(null);

  const dictation = useDictation({
    onTranscript: (text, caret) => {
      caretRef.current = caret;
      params.onTranscript(text);
    },
  });

  const onSendRef = useRef(params.onSend);
  onSendRef.current = params.onSend;
  const canSendRef = useRef(params.canSend);
  canSendRef.current = params.canSend;
  // A send was requested while a session was live: it runs once the session
  // ends and its transcript is in the composer.
  const sendPendingRef = useRef(false);

  const isActive = dictation.isActive;
  const dictationStop = dictation.stop;
  const dictationStart = dictation.start;
  const dictationCancel = dictation.cancel;

  // Land the caret just past what was dictated, so the user can keep typing there
  // — and so the next mic tap resumes at that very spot. Safe to do
  // synchronously: the last transcript emit arrived on an earlier turn of the
  // event loop and has already rendered, so the textarea holds the text this
  // offset indexes into.
  const placeCaret = useCallback(() => {
    const caret = caretRef.current;
    caretRef.current = null;
    if (caret === null) return;
    const el = textareaRef.current;
    if (el === null) return;
    el.setSelectionRange(caret, caret);
  }, [textareaRef]);

  const stop = useCallback(() => {
    dictationStop();
    placeCaret();
  }, [dictationStop, placeCaret]);

  // On the recorder path the transcript lands AFTER stop(), during the
  // `transcribing` beat — the offset its late emit records must still be
  // honoured. Watch the machine leave `transcribing` and place the caret then
  // (the streaming fallback never enters that state, and an abandoning cancel
  // clears the offset before this fires).
  const stateKind = dictation.state.kind;
  const prevKindRef = useRef(stateKind);
  // When `transcribing` began — the stop button morphs into the transcribing
  // cancel IN PLACE, so the second tap of an impatient double-tap on stop would
  // land on cancel and silently abandon the take it just submitted. Cancel taps
  // inside this grace window are swallowed; a deliberate abandon comes later.
  const transcribingSinceRef = useRef(0);
  useEffect(() => {
    const prev = prevKindRef.current;
    prevKindRef.current = stateKind;
    if (stateKind === "transcribing" && prev !== "transcribing") {
      transcribingSinceRef.current = Date.now();
    }
    if (prev === "transcribing" && stateKind === "idle") placeCaret();
    if (!sendPendingRef.current) return;
    // A send that finds nothing to send (a take too short to transcribe, an
    // empty transcript) is dropped on purpose: sending an empty question is
    // worse than a tap that does nothing. A failure already toasted its error.
    if (stateKind === "idle") {
      sendPendingRef.current = false;
      if (canSendRef.current()) onSendRef.current();
    } else if (stateKind === "error") {
      sendPendingRef.current = false;
    }
  }, [stateKind, placeCaret]);

  const cancel = useCallback(() => {
    sendPendingRef.current = false;
    // `cancel` rewinds the composer itself; its emit has NOT rendered yet, so
    // there is no offset to honour — just drop the one it recorded.
    dictationCancel();
    caretRef.current = null;
  }, [dictationCancel]);

  const onMicTap = useCallback(() => {
    if (isActive) {
      stop();
      return;
    }
    const el = textareaRef.current;
    if (el === null)
      throw new Error("should not happen: the composer textarea is unmounted");
    // The caret splits the text; a selection is what the transcript replaces.
    dictationStart({
      before: el.value.slice(0, el.selectionStart),
      after: el.value.slice(el.selectionEnd),
    });
  }, [isActive, stop, dictationStart, textareaRef]);

  const send = useCallback(() => {
    if (!isActive) {
      onSendRef.current();
      return;
    }
    // A repeated tap while the take is being finalized must not stop it again:
    // the second stop would abandon the transcript the first one is waiting for.
    if (sendPendingRef.current) return;
    sendPendingRef.current = true;
    stop();
  }, [isActive, stop]);

  const composer: ChatInputDictation | null = !dictation.isSupported
    ? null
    : dictation.state.kind === "listening"
      ? { kind: "listening", onStop: stop }
      : dictation.state.kind === "transcribing"
        ? {
            kind: "transcribing",
            onCancel: () => {
              // Double-tap-on-stop guard — see transcribingSinceRef above.
              if (
                Date.now() - transcribingSinceRef.current <
                TRANSCRIBING_CANCEL_GRACE_MS
              ) {
                return;
              }
              cancel();
            },
          }
        : dictation.state.kind === "connecting"
          ? { kind: "connecting", onCancel: cancel }
          : { kind: "idle", onStart: onMicTap };

  return {
    state: dictation.state,
    isSupported: dictation.isSupported,
    isActive,
    onMicTap,
    stop,
    send,
    cancel,
    composer,
    composerReadOnly:
      dictation.state.kind === "connecting" ||
      dictation.state.kind === "transcribing",
  };
}
